/**
 * Shop / platform helpers shared by template search filters and feed cards.
 * Search RPCs often omit `shop_link` or return JSON / comma-separated values.
 */

export function normalizeShopUrl(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    const lower = trimmed.toLowerCase();
    if (lower === "null" || lower === "undefined" || lower === "none") return "";
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        const parsed = JSON.parse(trimmed);
        const nested = normalizeShopUrl(parsed);
        if (nested) return nested;
      } catch {
        /* keep splitting below */
      }
    }
    const first = trimmed.split(",")[0]?.trim() ?? "";
    return first === trimmed ? first : normalizeShopUrl(first) || first;
  }
  if (Array.isArray(value)) {
    for (const entry of value) {
      const url = normalizeShopUrl(entry);
      if (url) return url;
    }
    return "";
  }
  if (typeof value === "object") {
    const rec = value as Record<string, unknown>;
    return (
      normalizeShopUrl(rec.url) ||
      normalizeShopUrl(rec.link) ||
      normalizeShopUrl(rec.shop_link) ||
      normalizeShopUrl(rec.href)
    );
  }
  return String(value).trim();
}

const SOCIAL_PLATFORMS = new Set([
  "instagram",
  "youtube",
  "tiktok",
  "pinterest",
  "twitter",
  "linkedin",
  "newsletter",
  "podcast",
]);

const AFFILIATE_PLATFORMS: Record<string, string> = {
  ltk: "LTK",
  amazon: "Amazon",
  shopmy: "ShopMy",
  misc: "MISC",
  "misc-content": "MISC-content",
};

/** Canonical platform id for FeedPost / PostInfo icons. */
export function normalizeFeedPlatform(platform: unknown): string {
  const raw = String(platform ?? "").trim();
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (lower === "x") return "twitter";
  if (SOCIAL_PLATFORMS.has(lower)) return lower;
  return AFFILIATE_PLATFORMS[lower] ?? raw;
}

const URL_PLATFORM_HINTS: Array<[string, string]> = [
  ["instagram.com", "instagram"],
  ["youtube.com", "youtube"],
  ["youtu.be", "youtube"],
  ["tiktok.com", "tiktok"],
  ["pinterest.", "pinterest"],
  ["linkedin.com", "linkedin"],
  ["twitter.com", "twitter"],
  ["x.com", "twitter"],
];

/** Icon key from platform, with permalink / shop URL fallback when platform is missing or odd. */
export function resolvePlatformIconKey(
  platform: unknown,
  permalink?: unknown,
  shopLink?: unknown,
): string {
  const normalized = normalizeFeedPlatform(platform);
  if (
    SOCIAL_PLATFORMS.has(normalized) ||
    normalized === "LTK" ||
    normalized === "Amazon" ||
    normalized === "ShopMy"
  ) {
    return normalized;
  }
  const fromType = inferFeedPlatformFromType(platform);
  if (fromType) return fromType;
  const blob = `${permalink ?? ""} ${shopLink ?? ""}`.toLowerCase();
  for (const [needle, key] of URL_PLATFORM_HINTS) {
    if (blob.includes(needle)) return key;
  }
  return normalized;
}

const CONTENT_TYPES = new Set([
  "REELS",
  "FEED",
  "STORY",
  "HIGHLIGHTS",
  "VIDEO",
  "IMAGE",
  "POST",
  "TIKTOK",
  "PIN",
  "TWEET",
  "MAIL",
  "PODCAST",
  "SHORTS",
  "SHORT",
  "LINKEDIN",
]);

/** When `platform` is missing, infer a FeedPost renderer from `type`. */
export function inferFeedPlatformFromType(type: unknown): string {
  const t = String(type ?? "").trim().toUpperCase();
  if (t === "TIKTOK") return "tiktok";
  if (t === "PIN") return "pinterest";
  if (t === "TWEET" || t === "TWITTER") return "twitter";
  if (t === "MAIL" || t === "NEWSLETTER") return "newsletter";
  if (t === "PODCAST") return "podcast";
  if (t === "LINKEDIN") return "linkedin";
  if (t === "SHORTS" || t === "SHORT" || t === "YOUTUBE") return "youtube";
  if (CONTENT_TYPES.has(t)) return "instagram";
  return "";
}

/** Collect possible post_id keys from a feed/search row. */
export function postIdKeys(
  post: { post_id?: unknown; id?: unknown; raw_content_id?: unknown } | null,
): string[] {
  if (!post) return [];
  const keys = new Set<string>();
  for (const value of [post.post_id, post.id, post.raw_content_id]) {
    if (value == null || value === "") continue;
    const s = String(value).trim().replace(/^["']+|["']+$/g, "");
    if (s) keys.add(s);
  }
  return [...keys];
}

export function lookupShopUrl(
  post: { post_id?: unknown; id?: unknown; raw_content_id?: unknown } | null,
  shopLinkOverrides?: Record<string, string>,
): string {
  if (!post || !shopLinkOverrides) return "";
  for (const id of postIdKeys(post)) {
    const hit = shopLinkOverrides[id];
    if (hit) return hit;
  }
  return "";
}

export type ShopButtonKind = "shop" | "external" | "tagged" | "permalink";

export type ShopButtonCta = {
  url: string;
  /** Shop link → Shop; external/tagged → View; source/permalink → Watch */
  label: "Shop" | "View" | "Watch";
  kind: ShopButtonKind;
};

type ShopButtonPostFields = {
  post_id?: unknown;
  id?: unknown;
  raw_content_id?: unknown;
  shop_link?: unknown;
  tagged_link?: unknown;
  external_link?: unknown;
  permalink?: unknown;
  table_url?: unknown;
  url?: unknown;
};

/** Instagram content paths — not person/profile mentions. */
const IG_CONTENT_PATH =
  /^\/(p|reel|reels|tv|stories|s|shopping|ar|explore)\b/i;
/**
 * Social “watch this post” paths — not commerce. When these show up in
 * `shop_link` / tagged / external they must not become a Shop button that
 * only opens Instagram (homepage pill already says Watch via permalink).
 * Keep `/shopping` out so real IG shopping links can still label Shop.
 */
const IG_WATCH_PATH = /^\/(p|reel|reels|tv|stories|s|ar|explore)\b/i;
/** TikTok / X / YouTube content paths (vs bare profile). */
const TIKTOK_CONTENT_PATH = /^\/@[^/]+\/(video|photo)\b/i;
const X_CONTENT_PATH = /^\/[^/]+\/(status|i\/)\b/i;
const YT_CONTENT_PATH =
  /^\/(watch|shorts|clip|embed|live|playlist|results)\b/i;
const YT_WATCH_PATH = /^\/(watch|shorts|clip|embed|live)\b/i;

/**
 * True when the URL (or bare handle) is a social person/profile mention —
 * not a product/shop URL and not a post/reel permalink.
 * Mention-only `tagged_link` values must not become homepage View pills.
 */
export function isSocialProfileUrl(value: unknown): boolean {
  const raw = normalizeShopUrl(value);
  if (!raw) return false;

  // Bare @handle or username with no domain — mention text, not a CTA URL.
  if (raw.startsWith("@")) return true;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(raw) && !raw.includes(".") && !raw.includes("/")) {
    return true;
  }

  let host = "";
  let path = "";
  try {
    const withProtocol = /^[a-z][a-z0-9+.-]*:/i.test(raw)
      ? raw
      : `https://${raw.replace(/^\/\//, "")}`;
    const u = new URL(withProtocol);
    host = u.hostname.replace(/^www\./i, "").toLowerCase();
    path = u.pathname || "/";
  } catch {
    return false;
  }

  if (host === "instagram.com" || host.endsWith(".instagram.com")) {
    if (IG_CONTENT_PATH.test(path)) return false;
    // /username or /username/ — profile from a mention/usertag
    const seg = path.replace(/^\/+|\/+$/g, "").split("/")[0] || "";
    if (!seg) return true; // instagram.com root
    return !/^(accounts|direct|about|developer|legal)$/i.test(seg);
  }

  if (host === "tiktok.com" || host.endsWith(".tiktok.com")) {
    if (TIKTOK_CONTENT_PATH.test(path)) return false;
    // /@user or /@user/ — profile
    return /^\/@[^/]+\/?$/i.test(path) || path === "/" || path === "";
  }

  if (
    host === "twitter.com" ||
    host === "x.com" ||
    host.endsWith(".twitter.com") ||
    host.endsWith(".x.com")
  ) {
    if (X_CONTENT_PATH.test(path)) return false;
    const seg = path.replace(/^\/+|\/+$/g, "").split("/")[0] || "";
    if (!seg) return true;
    return !/^(home|explore|search|i|settings|intent|share)$/i.test(seg);
  }

  if (
    host === "youtube.com" ||
    host === "youtu.be" ||
    host.endsWith(".youtube.com")
  ) {
    if (host === "youtu.be") return false; // short content links
    if (YT_CONTENT_PATH.test(path)) return false;
    // /@channel, /channel/…, /c/…, /user/… without a video id
    return /^\/(@|channel\/|c\/|user\/)/i.test(path);
  }

  return false;
}

/**
 * True when the URL is a social post/reel/video permalink (watch on IG/TT/YT/X),
 * not a product/shop destination.
 */
export function isSocialWatchUrl(value: unknown): boolean {
  const raw = normalizeShopUrl(value);
  if (!raw) return false;

  let host = "";
  let path = "";
  try {
    const withProtocol = /^[a-z][a-z0-9+.-]*:/i.test(raw)
      ? raw
      : `https://${raw.replace(/^\/\//, "")}`;
    const u = new URL(withProtocol);
    host = u.hostname.replace(/^www\./i, "").toLowerCase();
    path = u.pathname || "/";
  } catch {
    return false;
  }

  if (host === "instagram.com" || host.endsWith(".instagram.com")) {
    return IG_WATCH_PATH.test(path);
  }
  if (host === "tiktok.com" || host.endsWith(".tiktok.com")) {
    return TIKTOK_CONTENT_PATH.test(path);
  }
  if (
    host === "twitter.com" ||
    host === "x.com" ||
    host.endsWith(".twitter.com") ||
    host.endsWith(".x.com")
  ) {
    return X_CONTENT_PATH.test(path);
  }
  if (
    host === "youtube.com" ||
    host === "youtu.be" ||
    host.endsWith(".youtube.com")
  ) {
    if (host === "youtu.be") return true;
    return YT_WATCH_PATH.test(path);
  }
  return false;
}

function commerceOrContentUrl(value: unknown): string {
  const url = normalizeShopUrl(value);
  // Profiles and social watch permalinks are not shop destinations.
  if (!url || isSocialProfileUrl(url) || isSocialWatchUrl(url)) return "";
  return url;
}

/** True shop_link only (plus optional per-id backfill). Does not fall through to tagged/external. */
export function resolvePostShopLinkOnly(
  post: ShopButtonPostFields | null,
  shopLinkOverrides?: Record<string, string>,
): string {
  if (!post) return "";
  return (
    commerceOrContentUrl(post.shop_link) ||
    commerceOrContentUrl(lookupShopUrl(post, shopLinkOverrides))
  );
}

/** Non-empty shop_link text that is not a URL. Empty and null-like values do not count. */
function shopLinkText(value: unknown): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  const lower = trimmed.toLowerCase();
  if (lower === "null" || lower === "undefined" || lower === "none") return "";
  return trimmed;
}

/**
 * Website Shop button priority + label:
 * 1) shop_link → Shop
 * 2) external_link → View (Shop when forceShopLabel — Custom Products / isLinks)
 * 3) tagged_link → View (Shop when forceShopLabel)
 * 4) source / permalink (permalink, table_url, url) → Watch
 *
 * Social profile / mention URLs are skipped so View does not open Instagram
 * (or similar) just because a person was tagged. Social watch permalinks in
 * shop_link (reel/post/video) also skip Shop — same Watch label as the
 * homepage pill when the strip omits shop_link.
 */
export function resolveShopButtonCta(
  post: ShopButtonPostFields | null,
  shopLinkOverrides?: Record<string, string>,
  options?: { forceShopLabel?: boolean },
): ShopButtonCta | null {
  if (!post) return null;
  const forceShop = Boolean(options?.forceShopLabel);

  const shop = resolvePostShopLinkOnly(post, shopLinkOverrides);
  if (shop) return { url: shop, label: "Shop", kind: "shop" };

  // shop_link text that is not a URL (for example "sfhgdfgdfdgd") must still
  // label Shop. Otherwise the button falls through to the permalink and says Watch.
  // Skip http(s) / social watch values — those are handled as commerce or Watch.
  const shopTextCandidate =
    shopLinkText(post.shop_link) ||
    shopLinkText(lookupShopUrl(post, shopLinkOverrides));
  if (
    shopTextCandidate &&
    !/^https?:\/\//i.test(shopTextCandidate.trim()) &&
    !isSocialWatchUrl(shopTextCandidate)
  ) {
    return { url: shopTextCandidate, label: "Shop", kind: "shop" };
  }

  const external = commerceOrContentUrl(post.external_link);
  if (external) {
    return {
      url: external,
      label: forceShop ? "Shop" : "View",
      kind: "external",
    };
  }

  const tagged = commerceOrContentUrl(post.tagged_link);
  if (tagged) {
    return {
      url: tagged,
      label: forceShop ? "Shop" : "View",
      kind: "tagged",
    };
  }

  // Prefer true permalink; if shop_link was a social watch URL, use it as Watch.
  const permalink =
    normalizeShopUrl(post.permalink) ||
    normalizeShopUrl(post.table_url) ||
    normalizeShopUrl(post.url) ||
    (isSocialWatchUrl(post.shop_link)
      ? normalizeShopUrl(post.shop_link)
      : "") ||
    (isSocialWatchUrl(post.external_link)
      ? normalizeShopUrl(post.external_link)
      : "") ||
    (isSocialWatchUrl(post.tagged_link)
      ? normalizeShopUrl(post.tagged_link)
      : "");
  if (permalink) return { url: permalink, label: "Watch", kind: "permalink" };

  return null;
}

/** Commerce URL from the post row: shop_link → external_link → tagged_link
 *  (optional per-id map is a legacy backfill only). Permalink is not included. */
export function resolvePostShopUrl(
  post: ShopButtonPostFields | null,
  shopLinkOverrides?: Record<string, string>,
): string {
  if (!post) return "";
  return (
    resolvePostShopLinkOnly(post, shopLinkOverrides) ||
    commerceOrContentUrl(post.external_link) ||
    commerceOrContentUrl(post.tagged_link)
  );
}
